"use client";

import React from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../services/store";
import { formatPattern, extractPatternChars } from "./util-filter";
import { colorMapping } from "./util-color";
import SymbolIframe from "./SymbolIframe";
import { MouseOverPopover } from "./Popover";
import Candle from "./Candle";
import { addWatch } from "./util-api";
import {
  getSectorBySymbol,
  findMatchingSectorSymbols,
  SectorMap,
} from "./symbols";
import { Toast } from "./Toast";
import {
  sortSectorSymbolsByPercentChange,
  getBestSectorForSymbol,
} from "./util-symbol-grouping";

const StrongSymbolRow = observer(function StrongSymbolRow({
  sym,
  val,
  isPos,
  colCh = 10,
  hideDropdown = false,
  source,
}) {
  const store = useStore();
  const [open, setOpen] = React.useState(false);
  const [showSectorDropdown, setShowSectorDropdown] = React.useState(false);
  const [toastMsg, setToastMsg] = React.useState(null);
  const [toastType, setToastType] = React.useState("success");
  const [isMobile, setIsMobile] = React.useState(false);
  const [swipeOffset, setSwipeOffset] = React.useState(0);
  const swipeOffsetRef = React.useRef(0);
  const touchRef = React.useRef(null);
  const didSwipeRef = React.useRef(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const media = window.matchMedia("(max-width: 768px)");
    const updateMobile = () => {
      setIsMobile(media.matches);
      if (!media.matches) {
        swipeOffsetRef.current = 0;
        setSwipeOffset(0);
      }
    };

    updateMobile();
    if (media.addEventListener) media.addEventListener("change", updateMobile);
    else media.addListener(updateMobile);

    return () => {
      if (media.removeEventListener)
        media.removeEventListener("change", updateMobile);
      else media.removeListener(updateMobile);
    };
  }, []);

  const s = (sym || "").toLowerCase();
  const inPosSignals = store.signalPosSymbols?.has(s);
  const inNegSignals = store.signalNegSymbols?.has(s);
  const inSignals = inPosSignals || inNegSignals;

  //const pattern = formatPattern(val?.pattern || val);
  const pattern =
    typeof val === "string"
      ? formatPattern(val)
      : typeof val?.pattern === "string" && val.pattern
        ? formatPattern(val.pattern)
        : store.patternMap?.[s]
          ? formatPattern(store.patternMap[s])
        : undefined;
  const signalPattern =
    typeof val === "string"
      ? val
      : typeof val?.pattern === "string" && val.pattern
        ? val.pattern
        : store.patternMap?.[s] || "";

  const sector = val?.sector;
  const chipBg = colorMapping(sector, sym);

  // Find the actual sector this symbol belongs to (choose longest if multiple)
  const actualSector = React.useMemo(() => {
    if (!sym) return null;
    const s = sym.toLowerCase();
    const matchingSectors = [];

    // Find all sectors that contain this symbol
    for (const [sectorKey, sectorSymbols] of Object.entries(SectorMap)) {
      if (Array.isArray(sectorSymbols) && sectorSymbols.includes(s)) {
        matchingSectors.push(sectorKey);
      }
    }

    if (matchingSectors.length === 0) return null;
    if (matchingSectors.length === 1) return matchingSectors[0];

    // Choose the sector with the longest length (more fine-grained)
    return matchingSectors.reduce((longest, current) =>
      current.length > longest.length ? current : longest,
    );
  }, [sym]);

  // Get sector symbols for dropdown
  const sectorSymbols = React.useMemo(() => {
    if (!actualSector) return [];
    const symbols = findMatchingSectorSymbols(actualSector);
    const symbolsWithData = symbols.map((sym) => {
      const pattern = store.patternMap?.[sym] || "";
      const symbolSector = getBestSectorForSymbol(sym, actualSector);
      return [sym, { pattern, sector: symbolSector }];
    });
    return sortSectorSymbolsByPercentChange(
      symbolsWithData,
      store.currentGridMap,
      store.formattedDate,
    );
  }, [
    actualSector,
    store.patternMap,
    store.currentGridMap,
    store.formattedDate,
  ]);

  //console.log("AAA", val, sector, chipBg);
  const toggle = () => {
    if (source === "hotlist" && isMobile && swipeOffsetRef.current < 0) {
      swipeOffsetRef.current = 0;
      setSwipeOffset(0);
      return;
    }
    setOpen((o) => !o);
    store.setAllowShortCut(true);
    if (sector) {
      store.setSector(sector);
    }
  };

  const closeToast = () => setToastMsg(null);

  const handleRemove = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    await store.removeFromFavoriteList(sym.toLowerCase());
    const stillFavorite = store.favoriteList.some(
      (item) => item.toLowerCase() === s,
    );

    if (stillFavorite) {
      setToastType("error");
      setToastMsg(store.favoritesStore?.lastError || "Favorite delete failed");
    }
  };

  const updateSwipeOffset = (offset) => {
    const nextOffset = Math.max(-88, Math.min(0, offset));
    swipeOffsetRef.current = nextOffset;
    setSwipeOffset(nextOffset);
  };

  const handleTouchStart = (e) => {
    if (source !== "hotlist" || !isMobile) return;
    const touch = e.touches[0];
    touchRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      offset: swipeOffsetRef.current,
    };
    didSwipeRef.current = false;
  };

  const handleTouchMove = (e) => {
    if (!touchRef.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchRef.current.x;
    const dy = touch.clientY - touchRef.current.y;

    if (Math.abs(dx) <= Math.abs(dy)) return;
    if (Math.abs(dx) > 6) didSwipeRef.current = true;
    updateSwipeOffset(touchRef.current.offset + dx);
  };

  const handleTouchEnd = () => {
    if (!touchRef.current) return;
    updateSwipeOffset(swipeOffsetRef.current <= -40 ? -88 : 0);
    touchRef.current = null;
  };

  const handleBuy = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const sec = sector || getSectorBySymbol(sym);
    const curTick = store.gridMap?.[s]?.[0] ?? store.gridMap?.[sym]?.[0];
    const tick = curTick
      ? {
          ...curTick,
          symbol: s,
          lo: curTick.low,
          close: curTick.close,
          sector: sec,
        }
      : { symbol: s, sector: sec };

    addWatch(
      {
        ticks: [
          {
            ...tick,
            pattern: signalPattern,
            orderType: 1,
            signalStrength: 5,
          },
        ],
        date: store.formattedDate,
      },
      () => {
        // ✅ update store signalCount (and dot/highlight) immediately
        store.addSignalSymbol({
          symbol: s,
          orderType: 1,
          sector: sec,
          signalStrength: 5,
        });

        setToastType("success");
        setToastMsg(`Buy ${s.toUpperCase()} added`);
      },
    );
  };

  const handleSell = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const sec = sector || getSectorBySymbol(sym);
    const curTick = store.gridMap?.[s]?.[0] ?? store.gridMap?.[sym]?.[0];
    const tick = curTick
      ? {
          ...curTick,
          symbol: s,
          lo: curTick.low,
          close: curTick.close,
          sector: sec,
        }
      : { symbol: s, sector: sec };

    addWatch(
      {
        ticks: [
          {
            ...tick,
            pattern: signalPattern,
            orderType: -1,
            signalStrength: 5,
          },
        ],
        date: store.formattedDate,
      },
      () => {
        store.addSignalSymbol({
          symbol: s,
          orderType: -1,
          sector: sec,
          signalStrength: 5,
        });

        setToastType("success");
        setToastMsg(`Sell ${s.toUpperCase()} added`);
      },
    );
  };

  //get pct
  const { formattedDate: date } = store;
  const ticks = store.currentGridMap?.[s] || store.currentGridMap?.[sym] || [];
  const [today, prev] = ticks;

  const hasData = !!(
    today &&
    prev &&
    Number.isFinite(+today.close) &&
    Number.isFinite(+prev.close)
  );
  const close = hasData ? +today.close : null;
  const prevClose = hasData ? +prev.close : null;
  const delta = hasData ? close - prevClose : 0;
  const topPercentChange = Number(val?.percentChange);
  const hasTopPercentChange =
    source === "top" && Number.isFinite(topPercentChange);
  const pct = hasData && prevClose !== 0 ? (delta / prevClose) * 100 : null;
  const percentText = hasTopPercentChange
    ? `${topPercentChange >= 0 ? "+" : ""}${topPercentChange.toFixed(1)}%`
    : hasData && pct !== null
      ? `${delta >= 0 ? "+" : ""}${pct.toFixed(1)}%`
      : "";

  const upperSym = sym?.toUpperCase?.();
  const marketCap =
    store.marketCapMap?.[sym] ??
    store.marketCapMap?.[s] ??
    store.marketCapMap?.[upperSym];
  const hasMarketCap = Number.isFinite(+marketCap) && +marketCap > 0;
  const showMarketCapText = store.isMarketCapFilter && hasMarketCap;
  if (store.isMarketCapFilter && marketCap > 0 && marketCap < 40) {
    return null;
  }

  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          backgroundColor:
            source === "hotlist" && isMobile ? "#dc2626" : "transparent",
        }}
      >
        {source === "hotlist" && isMobile && (
          <button
            type="button"
            onClick={handleRemove}
            aria-label={`Remove ${sym.toUpperCase()} from favorites`}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: 88,
              border: 0,
              backgroundColor: "#dc2626",
              color: "white",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Delete
          </button>
        )}

        {/* row header */}
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (didSwipeRef.current) {
            didSwipeRef.current = false;
            e.preventDefault();
            return;
          }
          toggle();
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        /*
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        */
        title={sym}
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          touchAction: source === "hotlist" && isMobile ? "pan-y" : "auto",
          backgroundColor:
            source === "hotlist" && isMobile ? "rgb(24, 55, 39)" : "transparent",
          transform: `translateX(${swipeOffset}px)`,
          transition: touchRef.current ? "none" : "transform 160ms ease-out",
        }}
      >
        {/* content row must span full width */}
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            flex: 1,
            minWidth: 0,
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6, // space between pill and dot
            }}
          >
            {/* symbol pill */}
            <span
              style={{
                width: `${colCh}ch`,
                display: "inline-block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                backgroundColor: chipBg !== "none" ? chipBg : "white",
                borderRadius: 999,
                border: "1px solid #e5e7eb",
                textAlign: "center",
                flex: "0 0 auto",
                paddingTop: 5,
                paddingBottom: 5,
                color: "black",
              }}
            >
              <MouseOverPopover
                symbol={sym}
                sector={sector}
                tick={{ symbol: sym, sector: sector }}
                doHover={false}
              >
                {sym.toUpperCase()}
              </MouseOverPopover>
            </span>

            {/* ✅ BIG signal dot outside pill */}
            {inSignals && (
              <span
                title={inPosSignals ? "Signal +" : "Signal -"}
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: inPosSignals
                    ? "rgba(34,197,94,.9)"
                    : "rgba(239,68,68,.9)",
                  boxShadow: "0 0 0 2px rgba(0,0,0,.4)",
                  flex: "0 0 auto",
                }}
              />
            )}
          </span>
          {/* pattern pushed to the right */}
          {/* wrapper: pushes to the right and aligns items */}
          <span
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {/* ER value display when showER is enabled */}
            {store.showER && store.erMap?.[sym] && (
              <span
                style={{
                  color: "#9ca3af",
                  fontSize: 11,
                  marginRight: 6,
                }}
              >
                {store.erMap[sym]}
              </span>
            )}

            {/* original pill - click to show sector dropdown */}
            <span
              onClick={(e) => {
                if (
                  !hideDropdown &&
                  actualSector &&
                  sectorSymbols.length > 1 &&
                  store.allowDrillDown === true
                ) {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowSectorDropdown(!showSectorDropdown);
                  // Ensure iframe doesn't open when sector dropdown is toggled
                  if (!showSectorDropdown) {
                    setOpen(false);
                  }
                }
              }}
              style={{
                //backgroundColor: isPos ? "#e6ffed" : "#ffecec",
                padding: "0 4px",
                borderRadius: 4,
                color: "#f4f4f4",
                cursor:
                  !hideDropdown &&
                  actualSector &&
                  sectorSymbols.length > 1 &&
                  store.allowDrillDown === true
                    ? "pointer"
                    : "default",
              }}
              title={
                !hideDropdown &&
                actualSector &&
                sectorSymbols.length > 1 &&
                store.allowDrillDown === true
                  ? `Click to show ${actualSector} sector symbols`
                  : ""
              }
            >
              {/*store.isAdmin
                ? pattern
                : hasData
                ? `${delta >= 0 ? "+" : ""}${delta.toFixed(2)} (${pct.toFixed(
                    1
                  )}%)`
                : ""*/}

              {/* Show pattern when enabled in percentage mode */}
              {showMarketCapText && (
                <span
                  style={{
                    marginRight: "6px",
                    fontSize: "11px",
                    color: "#999",
                  }}
                >
                  {Math.round(+marketCap)}b
                </span>
              )}

              {store.isPercent && store.showPattern && pattern && (
                <span
                  style={{
                    marginRight: "6px",
                    fontSize: "11px",
                    color: "#999",
                    cursor: "pointer",
                  }}
                  title={pattern}
                >
                  {extractPatternChars(pattern)}
                </span>
              )}

              {!store.isPercent
                ? pattern
                : percentText}

              {/* Show V/VV pattern when isPercent is true and pattern contains V */}
              {store.isPercent &&
                !store.showPattern &&
                pattern &&
                (pattern.includes("V") || pattern.includes("v")) && (
                  <span
                    style={{
                      marginLeft: "4px",
                      fontSize: "11px",
                      color: "#999",
                    }}
                  >
                    {pattern.match(/V+|v+/g)?.[0] || ""}
                  </span>
                )}
            </span>

            {/* subtle text action next to candle */}
            {store.isAdmin &&
              store.isAdminParam &&
              store.allowSignal &&
              isPos && (
                <span
                  onClick={handleBuy}
                  title="Buy"
                  style={{
                    fontSize: 12,
                    lineHeight: 1,
                    opacity: 0.55,
                    cursor: "pointer",
                    userSelect: "none",
                    color: "rgba(34,197,94,.95)",
                    paddingRight: "8px",
                  }}
                >
                  B
                </span>
              )}

            {store.isAdmin &&
              store.isAdminParam &&
              store.allowSignal &&
              !isPos && (
                <span
                  onClick={handleSell}
                  title="Sell"
                  style={{
                    fontSize: 12,
                    lineHeight: 1,
                    opacity: 0.55,
                    cursor: "pointer",
                    userSelect: "none",
                    color: "rgba(239,68,68,.95)",
                    paddingRight: "8px",
                  }}
                >
                  S
                </span>
              )}

            {/* Remove button for hotlist */}
            {source === "hotlist" && !isMobile && (
              <span
                onClick={handleRemove}
                title={`Remove ${sym.toUpperCase()} from favorites`}
                style={{
                  fontSize: 12,
                  lineHeight: 1,
                  opacity: 0.55,
                  cursor: "pointer",
                  userSelect: "none",
                  color: "rgba(239,68,68,.95)",
                  paddingRight: "8px",
                }}
              >
                ×
              </span>
            )}

            <Candle symbol={sym} />
          </span>
        </div>

        {/* tiny chevron at far right */}
        <svg
          aria-hidden
          width="16"
          height="16"
          viewBox="0 0 24 24"
          style={{
            marginLeft: 6,
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 120ms ease",
            opacity: 0.6,
            flex: "0 0 auto",
          }}
        >
          <path
            d="M9 6l6 6-6 6"
            fill="none"
            stroke="#6b7280"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      </div>

      {/* dropdown iframe */}
      {open && <SymbolIframe symbol={sym} pattern={signalPattern} />}

      {/* sector dropdown */}
      {showSectorDropdown && (
        <div style={{ marginTop: 8 }}>
          <div
            className="font-mono text-sm whitespace-pre leading-5 near-white bg-near-black"
            style={{
              marginBottom: 8,
              padding: 8,
              paddingLeft: 12,
              borderRadius: 8,
              backgroundColor: "rgba(20,83,45,.3)",
              boxShadow: "0 2px 6px rgba(0,0,0,.25)",
              maxHeight: "300px",
              overflow: "auto",
            }}
          >
            {sectorSymbols.map(([symbolKey, valObj]) => (
              <StrongSymbolRow
                key={symbolKey}
                sym={symbolKey}
                val={valObj}
                isPos={true}
                colCh={10}
                hideDropdown={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* toast */}
      {toastMsg && (
        <Toast
          message={toastMsg}
          type={toastType}
          onClose={closeToast}
          duration={1500}
        />
      )}
    </div>
  );
});

export default StrongSymbolRow;
